<template>
  <div :class="['toolbar']" v-if="editMode">
    <button @click="toggleBold" class="bold" :class="{ active: editor.isActive('bold') }">B</button>
    <button @click="toggleItalic" class="italic" :class="{ active: editor.isActive('italic') }">I</button>
    <button @click="toggleStrike" class="line-through" :class="{ active: editor.isActive('strike') }">S</button>
    <button @click="toggleUnderline" class="underline" :class="{ active: editor.isActive('underline') }">U</button>
    <button @click="toggleCode" class="code" :class="{ active: editor.isActive('code') }">{}</button>

    <button @click="toggleBulletList" class="orderedList" :class="{ active: editor.isActive('bulletList') }">
      <span>• –––</span>
      <span>• –––</span>
      <span>• –––</span>
    </button>
    <button @click="toggleOrderedList" class="orderedList" :class="{ active: editor.isActive('orderedList') }">
      <span>1 –––</span>
      <span>2 –––</span>
      <span>3 –––</span>
    </button>

    <button @click="addOrEditLink" :class="{ active: editor.isActive('link') }">🌐</button>

    <button @click="toggleHeading(2)" class="ml-20 heading fw-black" :class="{ active: editor.isActive('heading', { level: 2 }) }">H2</button>
    <button @click="toggleHeading(3)" class="heading fw-bold" :class="{ active: editor.isActive('heading', { level: 3 }) }">H3</button>
    <button @click="toggleHeading(4)" class="heading fw-semibold" :class="{ active: editor.isActive('heading', { level: 4 }) }">H4</button>

    <button @click="editor.chain().focus().setHorizontalRule().run()" title="Add horizontal line">
      <span class="horizontal-line-icon">—</span>
    </button>
  </div>
</template>

<script>
export default {
  props: {
    editor: {
      type: Object,
      required: true
    },
    editMode: {
      type: Boolean,
      required: true
    },
    fullWidthHeaderColor: {
      type: String,
      default: "#e0e0e5"
    },
    copyTextColor: {
      type: String,
      default: "#e0e0e5"
    },
    textColor: {
      type: String,
      default: "#000000"
    },
    folderName: {
      type: String,
      default: "tutorials"
    }
  },
  data() {
    return {
      isToolbarFixed: false
    };
  },
  methods: {
    toggleCode() {
      this.editor.chain().focus().toggleCode().run();
    },
    toggleBold() {
      this.editor.chain().focus().toggleBold().run();
    },
    toggleItalic() {
      this.editor.chain().focus().toggleItalic().run();
    },
    toggleStrike() {
      this.editor.chain().focus().toggleStrike().run();
    },
    toggleUnderline() {
      this.editor.chain().focus().toggleUnderline().run();
    },
    toggleBulletList() {
      this.editor.chain().focus().toggleBulletList().run();
    },
    toggleOrderedList() {
      this.editor.chain().focus().toggleOrderedList().run();
    },
    addOrEditLink() {
      const previousUrl = this.editor.getAttributes('link').href;
      const url = prompt('Enter URL', previousUrl || 'https://');

      if (url === null) {
        return;
      }

      if (url === '') {
        this.editor.chain().focus().unsetLink().run();
      } else {
        this.editor.chain().focus().setLink({href: url}).run();
      }
    },
    toggleHeading(level) {
      this.editor.chain().focus().toggleHeading({level}).run();
    },
    toggleCopyText() {
      this.editor.chain().focus().toggleNode("copyText", "paragraph", {
        backgroundColor: this.copyTextColor
      }).run();
    },
    updateTextColor(event) {
      this.editor.chain().focus().setColor(this.textColor).run();
      this.$emit('update:textColor', event.target.value);
    },
    setTextAlign(alignment) {
      this.editor.chain().focus().setTextAlign(alignment).run();
    },
    async uploadImage(event, folderName) {
      const file = event.target.files[0];
      if (!file) return;

      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder", folderName);

      try {
        // const response = await fetch("http://localhost:8080/yandex/upload/photo", {
        // const response = await fetch("https://45.93.201.69/yandex/upload/photo", {
        const response = await fetch("https://back.rosters.ru/yandex/upload/photo", {
          method: "POST",
          body: formData
        });

        if (!response.ok) {
          throw new Error("Failed to upload image");
        }

        const {imageUrl} = await response.json();
        this.editor.chain().focus().setImage({src: imageUrl}).run();
      } catch (error) {
        console.error(error);
        alert("Image upload failed");
      }
    }
  },
  mounted() {
    window.addEventListener("scroll", this.handleScroll);
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  handleScroll() {
    const toolbarTop = this.$el.offsetTop;
    this.isToolbarFixed = window.scrollY > toolbarTop;
  }
};
</script>

<style scoped>

.toolbar button {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  border: none;
  padding: 0 10px;
  color: var(--my-dark);
  background-color: var(--my-white);
  cursor: pointer;
  border-radius: 5px;
  font-size: 20px !important;
  font-weight: 500;
  transition: background-color 0.3s;
}

.toolbar button:hover {
  background-color: var(--my-links);
}

.toolbar button.active {
  background-color: var(--my-links);
  color: var(--my-light);
}

.bold {
  font-weight: 800 !important;
}

.italic {
  font-style: italic;
}

.line-through {
  text-decoration: line-through;
}

.underline {
  text-decoration: underline;
}

button.orderedList {
  display: flex;
  font-size: 7px !important;
  line-height: 6px;
  justify-content: center;
  font-weight: 900;
  align-items: center;
}

button.text-align {
  display: flex;
  font-size: 12px !important;
  line-height: 6px;
  justify-content: center;
  font-weight: 900;
  align-items: center;
}

button.heading {
  font-size: 12px !important;
}

.image-upload {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 26px;
  background-color: var(--my-white);
  border-radius: 5px;
  padding-top: 2px;
}

.image-upload:hover {
  background-color: var(--my-links);
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.toolbar {
  display: flex;
  justify-content: flex-start;
  gap: 10px;
  margin-bottom: 20px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  height: 60px;
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background-color: #f5f5f5;
  padding: 12px 120px 12px 10px;
  z-index: 1200;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.toolbar > * {
  flex-shrink: 0;
  min-width: 36px;
}

.toolbar::-webkit-scrollbar {
  display: none;
}

.toolbar input.color-input {
  padding: 0;
  height: 36px;
  width: 36px;
  border-radius: 5px;
  border: none;
  cursor: pointer;
}

.toolbar input.color-input:hover {
  opacity: 90%;
}

.fullWidthHeader {
  border-radius: 5px 0 0 5px !important;
}

input.fullWidthHeader-color {
  margin-left: -10px;
  padding: 0;
  outline: none;
  border: none;
  border-radius: 0 5px 5px 0 !important;
  cursor: pointer;
  height: 36px;
  width: 36px;
}

.fullWidthHeader-color:hover {
  opacity: 90%;
}

@media (max-width: 900px) {
  .image-upload {
    padding-top: 0;
  }
}

.horizontal-line-icon {
  font-size: 18px;
  font-weight: bold;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

</style>
